import { useCallback, useEffect, useState } from 'react';

import { useDrag } from '../../hooks/useDrag';
import { cn, getModificationClassName } from '../../lib/css/className/className';

import { DragStateData, DrawerDragParams, DrawerRefs, DrawerTransformParams } from './types';

export const cls = cn('Drawer');

/**
 * Получение видимой пользователем высоты после перемещения шторки пальцем
 * @param node - контейнер со шторкой
 * @returns height - видимая высота шторки
 */
export function getVisibleHeight(node: HTMLElement) {
    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const parentNode = node.parentNode as HTMLElement;

    const position = node.getBoundingClientRect();
    const referencePosition = parentNode.getBoundingClientRect();

    const height = node.clientHeight;
    const heightAfterTransform = node.clientHeight - Math.abs(referencePosition.top - position.top);

    if (height === heightAfterTransform || Math.abs(heightAfterTransform) < 0.5) {
        return height;
    }

    return Math.min(height, heightAfterTransform);
}

/**
 * Обновление стиля максимального размера шторки
 * @param node - контейнер со шторкой
 * @param axis - ось анимирования шторки
 * @param maxSize - максимальный размер шторки (ширина или высота в зависимости от `direction`)
 */
function updateDrawerMaxSize(node: HTMLElement, axis: 'x' | 'y', maxSize?: string) {
    const param = axis === 'x' ? 'maxWidth' : 'maxHeight';

    if (!maxSize) {
        delete node.style[param];
        return;
    }

    node.style[param] = maxSize;
}

/**
 * Смена класса при входе в полноэкранный режим шторки, чтобы убрать borderRadius
 * @param node - контейнер со шторкой
 */
function updateFullScreenStyles(node?: HTMLElement) {
    if (!node || typeof window === 'undefined') {
        return;
    }

    // RAF нужен для кейса, когда у нас ресайзится экран, чтобы дождаться изменения высоты шторки
    // Проверить можно появлением клавиатуры в мобильном браузере
    setTimeout(() => {
        const height = getVisibleHeight(node);
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const viewportHeight = window.visualViewport
            ? Math.round(window.visualViewport.height)
            : Number.POSITIVE_INFINITY;
        const resultHeight = Math.min(viewportHeight, windowHeight);

        const className = getModificationClassName(cls, { hideBorders: true });
        node.classList.toggle(className, Math.abs(height - resultHeight) < 0.5);
    }, 10);
}

/**
 * Обновление стилей анимации открытия/закрытия шторки
 * @param node - контейнер со шторкой
 * @param axis - ось анимирования шторки
 * @param openProgress - прогресс открытия шторки (1 - открыта, 0 - закрыта)
 * @param directionRatio - коэффициент направления оси (-1 - лево, 1 - право)
 * @param movement - количество пикселей, на которые пользователь "оттянул" шторку.
 */
function updateTransformAnimation({
    node,
    axis,
    openProgress,
    directionRatio,
    movement,
}: { node: HTMLElement } & DrawerTransformParams) {
    if (openProgress === 1) {
        node.style.transform = '';
        return;
    }

    const translatePercent = (1 - openProgress) * 100 * directionRatio;

    if (movement) {
        const traslateMovement = movement * directionRatio;

        node.style.transform =
            axis === 'x' ? `translate3d(${traslateMovement}px,0,0)` : `translate3d(0,${traslateMovement}px,0)`;
    } else {
        node.style.transform =
            axis === 'x' ? `translate3d(${translatePercent}%,0,0)` : `translate3d(0,${translatePercent}%,0)`;
    }
}

/**
 * Обновление стилей и анимаций содержимого шторки
 * @param containerRef - ссылка на блок с контейнером (блок, который может менять свое положение)
 * @param axis - ось анимирования шторки
 * @param openProgress - прогресс открытия шторки (1 - открыта, 0 - закрыта)
 * @param directionRatio - коэффициент направления оси (-1 - лево, 1 - право)
 * @param maxSize - максимальный размер шторки (ширина или высота в зависимости от `direction`)
 * @param isFullscreen — занимает ли шторка весь вьюпорт
 * @param movement - прогресс
 */
function updateDrawerStyles({
    containerRef,
    axis,
    openProgress,
    directionRatio,
    maxSize,
    isFullscreen,
    movement,
}: Omit<DrawerRefs, 'contentRef'> & DrawerTransformParams & { maxSize?: string; isFullscreen?: boolean }) {
    if (!containerRef.current) {
        return;
    }

    updateTransformAnimation({
        node: containerRef.current,
        axis,
        openProgress,
        directionRatio,
        movement,
    });

    updateDrawerMaxSize(containerRef.current, axis, isFullscreen ? undefined : maxSize);
    updateFullScreenStyles(containerRef.current);
}

/**
 * Обработка перемещения шторки:
 * - обновляет анимации
 * - запрещает перемещения при scroll / отключении такой возможности
 * - возвращает события перемещения
 */
export function useDrawerDrag({
    isOpen = false,
    isDraggable,
    containerRef,
    contentRef,
    direction,
    onClose,
    maxSize,
    isFullscreen,
}: DrawerDragParams) {
    const [isDrawerOpened, setIsDrawerOpened] = useState(false);

    const drawerOpeningAxis = direction === 'bottom' ? 'y' : 'x';
    const directionRatio = direction === 'left' ? -1 : 1;

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Автоматический игнор в рамках DIRECT-312943
        setIsDrawerOpened(isOpen);
    }, [isOpen]);

    // прогресс открытия шторки от 0 до 1
    const setDrawerOpenProgress = useCallback(
        (progress: number, movement?: number) => {
            return updateDrawerStyles({
                containerRef,
                axis: drawerOpeningAxis,
                openProgress: progress,
                directionRatio,
                maxSize,
                isFullscreen,
                movement,
            });
        },
        [containerRef, directionRatio, drawerOpeningAxis, isFullscreen, maxSize],
    );

    useEffect(() => {
        setDrawerOpenProgress(isDrawerOpened ? 1 : 0);
    }, [isDrawerOpened, setDrawerOpenProgress]);

    const onViewportResize = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        updateFullScreenStyles(containerRef.current);
    }, [containerRef]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.visualViewport) {
            return;
        }

        window.visualViewport.addEventListener('resize', onViewportResize);

        return () => window.visualViewport?.removeEventListener('resize', onViewportResize);
    });

    const { dragProps } = useDrag<DragStateData>((dragState, setData) => {
        if (!isOpen || !contentRef.current) return;

        //  отключение перемещения шторки, если перемещение шторки запрещено
        if (!isDraggable) {
            return;
        }

        const { type, hadMove, event } = dragState;

        const drawerSize = drawerOpeningAxis === 'x' ? contentRef.current.clientWidth : contentRef.current.clientHeight;

        // если жест начался - запускается проверка на то, идет ли одновременно с жестом прокрутка контента
        if (type === 'start') {
            const initialScrollPosition = contentRef.current.scrollTop;

            const data: DragStateData = {
                ...dragState.data,
                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                isTargetUnderContent: contentRef.current.contains(event.target as HTMLElement),
                initialScrollPosition,
                isScrolled: initialScrollPosition !== 0,
            };

            setData(data);
        } else {
            const data: DragStateData = {
                ...dragState.data,
                isScrolled: Boolean(
                    dragState.data?.isScrolled ||
                        (dragState.data?.initialScrollPosition ?? 0) - contentRef.current.scrollTop < 0,
                ),
            };

            setData(data);
        }

        // отключение перемещения шторки, если жест происходит одновременно с прокруткой контента
        if (dragState.data?.isTargetUnderContent && dragState.data?.isScrolled) {
            return;
        }

        // если жест завершен - шторка возвращается в открытое положение либо закрывается
        // действие зависит от скорости жеста
        if (type === 'end' && hadMove) {
            const {
                velocity: { x: vx, y: vy },
                movement: { x: mx, y: my },
            } = dragState;

            const movement = directionRatio * (drawerOpeningAxis === 'x' ? mx : my);
            const velocity = directionRatio * (drawerOpeningAxis === 'x' ? vx : vy);

            if (Math.abs(velocity) >= 0.1) {
                return velocity > 0 ? onClose?.() : setDrawerOpenProgress(1);
            } else if (movement / drawerSize >= 0.5) {
                return onClose?.();
            }

            return setDrawerOpenProgress(1);
        }

        // перемещение шторки вслед за жестом
        if (hadMove) {
            const {
                movement: { x: mx, y: my },
            } = dragState;

            const movement = directionRatio * (drawerOpeningAxis === 'x' ? mx : my);

            if (movement > 0) {
                const progress = Math.max(0, 1 - movement / drawerSize);

                if (progress < 0.1) {
                    return onClose?.();
                }

                return setDrawerOpenProgress(progress, movement);
            }
        }
    });

    return { dragProps };
}
